import { useEffect, useRef, useState } from 'react';
import api from '../../lib/api';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import { Users, ChevronDown, Building2, User, Mail } from 'lucide-react';
import { STATUS_LABELS } from '../../lib/utils';

const pretty = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const DEFAULT_AUD = { crm: { enabled: false, contact_types: [], partnership_types: [], statuses: [], tags: [], owners: [], industries: [], sources: [] }, include_newsletter: false, manual: [], excluded_emails: [] };

function FilterMultiSelect({ label, options, selected, onChange, labelFn, testid }) {
  const sel = selected || [];
  const toggle = (v) => onChange(sel.includes(v) ? sel.filter((x) => x !== v) : [...sel, v]);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className={`h-8 text-xs ${sel.length ? 'border-magenta text-magenta' : 'text-slate-600'}`} data-testid={testid}>
          {label}{sel.length > 0 && ` (${sel.length})`} <ChevronDown className="w-3 h-3 ml-1" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 max-h-72 overflow-auto p-2" align="start">
        {options.length === 0 && <p className="text-xs text-slate-400 p-2">No values in CRM yet</p>}
        {options.map((o) => {
          const v = typeof o === 'object' ? o.id : o;
          const lbl = typeof o === 'object' ? o.name : (labelFn ? labelFn(o) : pretty(o));
          return (
            <label key={v} className="flex items-center gap-2 text-sm px-2 py-1 rounded hover:bg-slate-50 cursor-pointer">
              <input type="checkbox" checked={sel.includes(v)} onChange={() => toggle(v)} className="accent-[#C2185B]" data-testid={`${testid}-opt-${v}`} />
              {lbl}
            </label>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export default function RecipientPicker({ audience, onChange }) {
  const [filters, setFilters] = useState(null);
  const [preview, setPreview] = useState(null);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState(null);
  const [emailCheck, setEmailCheck] = useState(null);
  const timer = useRef();

  const aud = { ...DEFAULT_AUD, ...(audience || {}), crm: { ...DEFAULT_AUD.crm, ...((audience || {}).crm || {}) } };
  const setCrm = (patch) => onChange({ ...aud, crm: { ...aud.crm, ...patch } });

  useEffect(() => { api.get('/comms/crm-filters').then((r) => setFilters(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      api.post('/comms/recipients-preview', { audience: aud }).then((r) => setPreview(r.data)).catch(() => {});
    }, 400);
    return () => clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(aud)]);

  useEffect(() => {
    if (search.trim().length < 2) { setResults(null); setEmailCheck(null); return; }
    const t = setTimeout(() => {
      api.get('/comms/contact-search', { params: { q: search } }).then((r) => setResults(r.data)).catch(() => {});
      if (EMAIL_RX.test(search.trim())) {
        api.get('/comms/check-email', { params: { email: search.trim() } }).then((r) => setEmailCheck(r.data)).catch(() => setEmailCheck(null));
      } else setEmailCheck(null);
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const addManual = (item) => {
    if ((aud.manual || []).some((m) => m.kind === item.kind && m.id === item.id)) return;
    onChange({ ...aud, manual: [...(aud.manual || []), { kind: item.kind, id: item.id, label: item.kind === 'company' ? `${item.name} (company)` : `${item.name} — ${item.email || item.company || ''}` }] });
    setSearch(''); setResults(null);
  };
  const removeManual = (m) => onChange({ ...aud, manual: aud.manual.filter((x) => !(x.kind === m.kind && x.id === m.id)) });
  const excludeEmail = (email) => onChange({ ...aud, excluded_emails: [...(aud.excluded_emails || []), email] });
  const unexclude = (email) => onChange({ ...aud, excluded_emails: (aud.excluded_emails || []).filter((e) => e !== email) });

  if (!filters) return <div className="text-xs text-slate-400">Loading CRM filters…</div>;

  return (
    <div className="space-y-4" data-testid="recipient-picker">
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Switch checked={!!aud.crm.enabled} onCheckedChange={(v) => setCrm({ enabled: v })} data-testid="crm-recipients-toggle" />
          <span className="text-sm font-semibold text-navy">CRM Recipient Builder</span>
          <span className="text-[11px] text-slate-400">({filters.crm_contacts_with_email} CRM contacts with email — live from CRM, single source of truth)</span>
        </div>
        {aud.crm.enabled && (
          <div className="flex flex-wrap gap-1.5 pl-1" data-testid="crm-filter-row">
            <FilterMultiSelect label="Contact Type" options={filters.contact_types || []} selected={aud.crm.contact_types} onChange={(v) => setCrm({ contact_types: v })} labelFn={(t) => t} testid="filter-contact-type" />
            <FilterMultiSelect label="Partnership Type" options={filters.partnership_types} selected={aud.crm.partnership_types} onChange={(v) => setCrm({ partnership_types: v })} testid="filter-partnership-type" />
            <FilterMultiSelect label="Status" options={filters.statuses} selected={aud.crm.statuses} onChange={(v) => setCrm({ statuses: v })} labelFn={(s) => STATUS_LABELS[s] || pretty(s)} testid="filter-status" />
            <FilterMultiSelect label="Tags" options={filters.tags} selected={aud.crm.tags} onChange={(v) => setCrm({ tags: v })} labelFn={(t) => t} testid="filter-tags" />
            <FilterMultiSelect label="Owner" options={filters.owners} selected={aud.crm.owners} onChange={(v) => setCrm({ owners: v })} testid="filter-owner" />
            <FilterMultiSelect label="Industry" options={filters.industries} selected={aud.crm.industries} onChange={(v) => setCrm({ industries: v })} labelFn={(t) => t} testid="filter-industry" />
            <FilterMultiSelect label="Source" options={filters.sources} selected={aud.crm.sources} onChange={(v) => setCrm({ sources: v })} labelFn={(t) => t} testid="filter-source" />
          </div>
        )}
        {aud.crm.enabled && <p className="text-[11px] text-slate-400 mt-1.5 pl-1">No filters selected = ALL CRM contacts with an email. Filters combine: match ANY value within a filter, ALL filters across.</p>}
      </div>

      <div className="flex items-center gap-2">
        <Switch checked={!!aud.include_newsletter} onCheckedChange={(v) => onChange({ ...aud, include_newsletter: v })} data-testid="newsletter-toggle" />
        <span className="text-sm font-semibold text-navy">Include Newsletter Subscribers</span>
        <span className="text-[11px] text-slate-400">(external contacts not in the CRM)</span>
      </div>

      <div>
        <div className="text-sm font-semibold text-navy mb-1.5">Manual Contact Selection</div>
        <Input placeholder="Search by name, company, email, phone, tag, contact type or partnership type…" value={search} onChange={(e) => setSearch(e.target.value)} className="h-9" data-testid="manual-subscriber-search" />
        {results && (
          <div className="border border-slate-200 rounded-lg mt-1 divide-y max-h-56 overflow-auto bg-white shadow-sm" data-testid="contact-search-results">
            {results.companies.map((c) => (
              <button key={`co-${c.id}`} type="button" onClick={() => addManual(c)} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2" data-testid={`search-company-${c.id}`}>
                <Building2 className="w-3.5 h-3.5 text-magenta shrink-0" />
                <span className="font-medium">{c.name}</span>
                <span className="text-xs text-slate-400 ml-auto">Entire company · {c.contact_count} contact{c.contact_count === 1 ? '' : 's'}</span>
              </button>
            ))}
            {results.contacts.map((c) => (
              <button key={`ld-${c.id}`} type="button" onClick={() => c.has_email && addManual(c)} disabled={!c.has_email}
                className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${c.has_email ? 'hover:bg-slate-50' : 'opacity-50 cursor-not-allowed'}`} data-testid={`search-contact-${c.id}`}>
                <User className="w-3.5 h-3.5 text-navy shrink-0" />
                <span className="font-medium">{c.name}</span>
                <span className="text-xs text-slate-500">{c.company}</span>
                <span className="text-xs text-slate-400 ml-auto">{c.has_email ? c.email : 'no email'}{c.contact_type ? ` · ${c.contact_type}` : ''}{c.partnership_type ? ` · ${pretty(c.partnership_type)}` : ''}</span>
              </button>
            ))}
            {results.subscribers.map((s) => (
              <button key={`sb-${s.id}`} type="button" onClick={() => addManual(s)} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2" data-testid={`search-subscriber-${s.id}`}>
                <Mail className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                <span className="font-medium">{s.name || s.email}</span>
                <span className="text-xs text-slate-400 ml-auto">Newsletter · {s.email}</span>
              </button>
            ))}
            {emailCheck?.valid && !emailCheck.exists && !emailCheck.suppressed && (
              <button type="button" onClick={() => addManual({ kind: 'email', id: emailCheck.email, name: emailCheck.email, email: emailCheck.email })}
                className="w-full text-left px-3 py-2 text-sm hover:bg-emerald-50 flex items-center gap-2" data-testid="add-raw-email">
                <Mail className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                <span className="font-medium">Send to {emailCheck.email}</span>
                <span className="text-[10px] font-bold uppercase tracking-wide bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded ml-auto">New — CRM contact will be created</span>
              </button>
            )}
            {emailCheck?.valid && emailCheck.exists && (
              <div className="px-3 py-2 text-[11px] text-slate-500 bg-sky-50" data-testid="email-exists-hint">
                ✓ {emailCheck.email} is an existing {emailCheck.kind === 'lead' ? 'CRM contact' : 'newsletter subscriber'}{emailCheck.name ? ` (${emailCheck.name}${emailCheck.company ? `, ${emailCheck.company}` : ''})` : ''} — select above to attach activity to their record.
              </div>
            )}
            {emailCheck?.suppressed && (
              <div className="px-3 py-2 text-[11px] text-rose-500 bg-rose-50" data-testid="email-suppressed-hint">
                {emailCheck.email} is on the suppression list (bounced/unsubscribed) and cannot receive emails.
              </div>
            )}
            {results.companies.length + results.contacts.length + results.subscribers.length === 0 && !emailCheck?.valid && (
              <p className="text-xs text-slate-400 px-3 py-2">No matches in CRM or newsletter subscribers. Type a full email address to send to someone new.</p>
            )}
          </div>
        )}
        {(aud.manual || []).length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2" data-testid="manual-chips">
            {aud.manual.map((m) => (
              <span key={`${m.kind}-${m.id}`} className={`text-xs rounded-full px-2.5 py-1 flex items-center gap-1 ${m.kind === 'company' ? 'bg-magenta/10 text-magenta' : 'bg-slate-100 text-slate-700'}`}>
                {m.label || m.id}
                <button type="button" onClick={() => removeManual(m)} className="text-slate-400 hover:text-red-500" data-testid={`remove-manual-${m.id}`}>×</button>
              </span>
            ))}
          </div>
        )}
      </div>

      {(aud.excluded_emails || []).length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="excluded-chips">
          <span className="text-xs font-semibold text-red-600">Excluded:</span>
          {aud.excluded_emails.map((e) => (
            <span key={e} className="text-xs bg-red-50 border border-red-200 text-red-600 rounded-full px-2.5 py-1 flex items-center gap-1">
              {e} <button type="button" onClick={() => unexclude(e)} className="hover:text-red-800">×</button>
            </span>
          ))}
        </div>
      )}

      <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5" data-testid="recipient-count">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-magenta" />
          <span className="text-sm font-semibold text-navy">{preview ? `${preview.count} recipients` : 'Calculating…'}</span>
          {preview?.crm_matched > 0 && <span className="text-xs text-slate-500">({preview.crm_matched} from CRM)</span>}
          <span className="text-[11px] text-slate-400 ml-auto">Unsubscribed & suppressed excluded automatically</span>
        </div>
        {preview?.sample?.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1" data-testid="recipient-sample">
            {preview.sample.map((r) => (
              <span key={r.email} className="text-[11px] bg-white border border-slate-200 rounded-full px-2 py-0.5 flex items-center gap-1">
                {r.first_name} {r.last_name} · {r.email}
                <button type="button" title="Exclude from this campaign" onClick={() => excludeEmail(r.email)} className="text-slate-300 hover:text-red-500" data-testid={`exclude-${r.email}`}>×</button>
              </span>
            ))}
            {preview.count > preview.sample.length && <span className="text-[11px] text-slate-400 px-1 py-0.5">+{preview.count - preview.sample.length} more</span>}
          </div>
        )}
      </div>
    </div>
  );
}
